import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import type { ThemeId, UpdateRegistryRequest } from '@lds/shared'
import { registryApi, imageApi, ApiError } from '../../lib/api'
import { useAuth } from '../../lib/auth'

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window === 'undefined' ? false : window.matchMedia('(max-width: 760px)').matches,
  )
  useEffect(() => {
    const mql = window.matchMedia('(max-width: 760px)')
    const onChange = () => setIsMobile(mql.matches)
    onChange()
    mql.addEventListener('change', onChange)
    return () => mql.removeEventListener('change', onChange)
  }, [])
  return isMobile
}

function isEmail(s: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim())
}

function fmtIban(s: string) {
  return s
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .replace(/(.{4})/g, '$1 ')
    .trim()
}

function normNullable(v: string) {
  const s = v.trim()
  return s === '' ? null : s
}

type SaveState = 'idle' | 'saving' | 'saved' | 'error'

function SaveBar({
  state,
  label = 'Enregistrer cette section',
  error,
  onSave,
}: {
  state: SaveState
  label?: string
  error?: string | null
  onSave: () => void
}) {
  return (
    <div className="os-savebar">
      <button
        type="button"
        className="btn-primary btn-sm"
        onClick={onSave}
        disabled={state === 'saving'}
      >
        {state === 'saving' ? 'Enregistrement…' : label}
      </button>
      <span
        className={`os-saved ${state === 'saved' ? 'os-saved-on' : ''}`}
        role="status"
        aria-live="polite"
      >
        Enregistré ✓
      </span>
      {state === 'error' && error && <span className="os-saveerr">{error}</span>}
    </div>
  )
}

function Section({
  id,
  eyebrow,
  title,
  sub,
  isMobile,
  defaultOpen,
  children,
}: {
  id: string
  eyebrow: string
  title: string
  sub?: string
  isMobile: boolean
  defaultOpen?: boolean
  children: ReactNode
}) {
  if (isMobile) {
    return (
      <details
        id={`sec-${id}`}
        className="os-sec os-sec-collapsible"
        open={defaultOpen ? true : undefined}
      >
        <summary className="os-sec-sum">
          <span className="os-sec-eyebrow">{eyebrow}</span>
          <span className="os-sec-title-m">{title}</span>
          <span className="os-sec-chev" aria-hidden="true">
            <svg viewBox="0 0 12 12" width="12" height="12">
              <path
                d="M3 4.5 L6 7.5 L9 4.5"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        </summary>
        <div className="os-sec-body">
          {sub && <p className="os-sec-sub">{sub}</p>}
          {children}
        </div>
      </details>
    )
  }

  return (
    <section id={`sec-${id}`} className="os-sec">
      <header className="os-sec-head">
        <p className="os-sec-eyebrow">{eyebrow}</p>
        <h2 className="os-sec-title">{title}</h2>
      </header>
      <div className="os-sec-body">
        {sub && <p className="os-sec-sub">{sub}</p>}
        {children}
      </div>
    </section>
  )
}

const THEME_OPTIONS: Array<{
  id: ThemeId
  name: string
  sub: string
  preview: { bg: string; ink: string; accent: string; fontFamily: string }
}> = [
  {
    id: 'editorial',
    name: 'Éditorial',
    sub: 'Fraunces · papier',
    preview: {
      bg: '#FAF6F0',
      ink: '#2B2420',
      accent: '#C97F6E',
      fontFamily: "'Fraunces', Georgia, serif",
    },
  },
  {
    id: 'minimal',
    name: 'Minimal',
    sub: 'Inter · blanc',
    preview: {
      bg: '#ffffff',
      ink: '#111111',
      accent: '#111111',
      fontFamily: 'ui-sans-serif, system-ui',
    },
  },
  {
    id: 'playful',
    name: 'Playful',
    sub: 'Nunito · rose',
    preview: {
      bg: '#fff7fb',
      ink: '#1f1b15',
      accent: '#d35d90',
      fontFamily: 'ui-sans-serif, system-ui',
    },
  },
]

const ACCENTS: Array<{ name: string; value: string }> = [
  { name: 'Sauge', value: '#7B8B6F' },
  { name: 'Argile', value: '#C97F6E' },
  { name: 'Ardoise', value: '#5C6970' },
  { name: 'Ocre', value: '#9C7C5C' },
  { name: 'Eucalyptus', value: '#6F8B82' },
  { name: 'Prune', value: '#7E6B8E' },
]

export default function Settings() {
  const { rid } = useParams<{ rid: string }>()
  const nav = useNavigate()
  const auth = useAuth()
  const isMobile = useIsMobile()

  const [loading, setLoading] = useState(true)
  const [loadErr, setLoadErr] = useState<string | null>(null)

  const [regTitle, setRegTitle] = useState('')
  const [dueDate, setDueDate] = useState<string>('')
  const [welcomeMessage, setWelcomeMessage] = useState('')
  const [coverUrl, setCoverUrl] = useState<string | null>(null)
  const coverFileRef = useRef<HTMLInputElement | null>(null)

  const [iban, setIban] = useState('')
  const [bic, setBic] = useState('')
  const [ibanHolder, setIbanHolder] = useState('')
  const [lydiaPhone, setLydiaPhone] = useState('')
  const [weroPhone, setWeroPhone] = useState('')
  const [paypalEmail, setPaypalEmail] = useState('')

  const [members, setMembers] = useState<
    Array<{ userId: string; email: string; displayName: string | null }>
  >([])
  const [pendingInvites, setPendingInvites] = useState<Array<{ id: string; email: string }>>([])
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteErr, setInviteErr] = useState<string | null>(null)
  const [teamActionErr, setTeamActionErr] = useState<string | null>(null)

  const [theme, setTheme] = useState<ThemeId>('editorial')
  const [accentColor, setAccentColor] = useState<string | null>(null)

  const [extraEmails, setExtraEmails] = useState<string[]>([])
  const [extraDraft, setExtraDraft] = useState('')
  const [extraErr, setExtraErr] = useState<string | null>(null)
  const extraInputRef = useRef<HTMLInputElement | null>(null)

  const [slug, setSlug] = useState('')
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState('')
  const [deleteErr, setDeleteErr] = useState<string | null>(null)

  const [saveGeneral, setSaveGeneral] = useState<SaveState>('idle')
  const [savePayment, setSavePayment] = useState<SaveState>('idle')
  const [saveTeam, setSaveTeam] = useState<SaveState>('idle')
  const [saveTheme, setSaveTheme] = useState<SaveState>('idle')
  const [saveNotif, setSaveNotif] = useState<SaveState>('idle')

  const userId = auth.status === 'authenticated' ? auth.me.user.id : undefined
  const meEmail = auth.status === 'authenticated' ? auth.me.user.email : undefined

  const refresh = useCallback(async () => {
    if (!rid) return
    const r = await registryApi.get(rid)
    setSlug(r.slug)

    setRegTitle(r.title)
    setDueDate(r.dueDate ?? '')
    setWelcomeMessage(r.welcomeMessage ?? '')
    setCoverUrl(r.coverImageUrl)

    setTheme(r.theme)
    setAccentColor(r.accentColor)

    setIban(r.paymentMethods.iban ?? '')
    setBic(r.paymentMethods.bic ?? '')
    setIbanHolder(r.paymentMethods.ibanHolder ?? '')
    setLydiaPhone(r.paymentMethods.lydiaPhone ?? '')
    setWeroPhone(r.paymentMethods.weroPhone ?? '')
    setPaypalEmail(r.paymentMethods.paypalEmail ?? '')

    setExtraEmails(r.extraNotificationEmails)
    setMembers(r.members)
    setPendingInvites(r.pendingInvites)
  }, [rid])

  useEffect(() => {
    if (!rid) return
    setLoading(true)
    setLoadErr(null)
    void (async () => {
      try {
        await refresh()
      } catch {
        setLoadErr('Impossible de charger les réglages.')
      } finally {
        setLoading(false)
      }
    })()
  }, [rid, refresh])

  function scheduleSaved(set: (s: SaveState) => void) {
    set('saved')
    window.setTimeout(() => set('idle'), 2200)
  }

  async function savePatch(set: (s: SaveState) => void, patch: UpdateRegistryRequest) {
    if (!rid) return
    set('saving')
    try {
      await registryApi.update(rid, patch)
      await refresh()
      scheduleSaved(set)
    } catch {
      set('error')
      window.setTimeout(() => set('idle'), 2600)
    }
  }

  async function onUploadCover(file: File) {
    if (!rid) return
    setSaveGeneral('saving')
    try {
      const up = await imageApi.upload(file, rid)
      setCoverUrl(up.url)
      await registryApi.update(rid, { coverImageId: up.id })
      await refresh()
      scheduleSaved(setSaveGeneral)
    } catch {
      setSaveGeneral('error')
      window.setTimeout(() => setSaveGeneral('idle'), 2600)
    }
  }

  function notifCommitDraft() {
    const raw = extraDraft.trim().replace(/,$/, '').trim()
    if (!raw) return
    if (!isEmail(raw)) {
      setExtraErr('Adresse email invalide.')
      return
    }
    if (extraEmails.includes(raw)) {
      setExtraDraft('')
      return
    }
    if (extraEmails.length >= 10) {
      setExtraErr('Maximum 10 adresses.')
      return
    }
    setExtraEmails((arr) => [...arr, raw])
    setExtraDraft('')
    setExtraErr(null)
  }

  const deleteMatches = useMemo(
    () => deleteConfirm === slug && slug.length > 0,
    [deleteConfirm, slug],
  )

  async function doDelete() {
    if (!rid || !deleteMatches) return
    setDeleteErr(null)
    try {
      await registryApi.delete(rid)
      nav('/me')
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        setDeleteErr('Impossible de supprimer: la liste a plusieurs propriétaires.')
      } else {
        setDeleteErr('Suppression impossible.')
      }
    }
  }

  async function invite() {
    if (!rid) return
    setInviteErr(null)
    setTeamActionErr(null)
    const email = inviteEmail.trim().toLowerCase()
    if (!isEmail(email)) {
      setInviteErr('Adresse email invalide.')
      return
    }
    setSaveTeam('saving')
    try {
      await registryApi.inviteCoOwner(rid, email)
      setInviteEmail('')
      await refresh()
      scheduleSaved(setSaveTeam)
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        setTeamActionErr("Impossible d'inviter: liste déjà à deux ou invitation existante.")
      } else {
        setTeamActionErr("Échec de l'envoi de l'invitation.")
      }
      setSaveTeam('error')
      window.setTimeout(() => setSaveTeam('idle'), 2600)
    }
  }

  async function cancelInvite(invId: string) {
    if (!rid) return
    setTeamActionErr(null)
    setSaveTeam('saving')
    try {
      await registryApi.cancelInvite(rid, invId)
      await refresh()
      scheduleSaved(setSaveTeam)
    } catch {
      setTeamActionErr("Impossible d'annuler l'invitation.")
      setSaveTeam('error')
      window.setTimeout(() => setSaveTeam('idle'), 2600)
    }
  }

  async function removeMember(memberId: string) {
    if (!rid) return
    setTeamActionErr(null)
    setSaveTeam('saving')
    try {
      await registryApi.removeMember(rid, memberId)
      await refresh()
      scheduleSaved(setSaveTeam)
      if (memberId === userId) nav('/me')
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) setTeamActionErr('Impossible: dernier membre.')
      else setTeamActionErr('Impossible de retirer ce membre.')
      setSaveTeam('error')
      window.setTimeout(() => setSaveTeam('idle'), 2600)
    }
  }

  if (!rid) return <main style={{ padding: '2rem' }}>Réglages introuvables.</main>
  if (loading) return <main style={{ padding: '2rem' }}>Chargement…</main>
  if (loadErr) return <main style={{ padding: '2rem' }}>{loadErr}</main>

  const titleLine = `${regTitle || 'Liste'} · réglages`

  return (
    <div className="owner-registry-settings os-page">
      <header className="os-chrome">
        <div className="os-chrome-inner">
          <div className="os-wordmark" aria-label="Liste de Naissance">
            <span className="os-wordmark-mark">·</span>
            <span className="os-wordmark-text">Liste de Naissance</span>
          </div>
          <nav className="os-bread" aria-label="Fil d'ariane">
            <Link to="/me" className="os-bread-link">
              Mes listes
            </Link>
            <span className="os-bread-sep" aria-hidden="true">
              /
            </span>
            <Link to={`/me/r/${rid}`} className="os-bread-link">
              {regTitle || 'Liste'}
            </Link>
            <span className="os-bread-sep" aria-hidden="true">
              /
            </span>
            <span className="os-bread-current">Réglages</span>
          </nav>
        </div>
      </header>

      <div className="os-body">
        <main className="os-main">
          <div className="os-canvas">
            {isMobile && (
              <Link className="os-backlink" to={`/me/r/${rid}`}>
                ← Retour à la liste
              </Link>
            )}

            <header className="os-head">
              <p className="eyebrow">{titleLine}</p>
              <h1 className="os-title">Tout est réglable.</h1>
              <p className="os-sub">
                Modifiez n&apos;importe quel détail — chaque section s&apos;enregistre
                indépendamment.
              </p>
            </header>

            <Section
              id="general"
              eyebrow="01"
              title="Général"
              sub="Le nom, la date prévue, le mot d'accueil et l'image de couverture."
              isMobile={isMobile}
              defaultOpen
            >
              <div className="os-grid2">
                <label className="field">
                  <span className="field-label">Titre de la liste</span>
                  <input
                    className="input"
                    value={regTitle}
                    onChange={(e) => setRegTitle(e.target.value)}
                  />
                  <p className="field-hint">Affiché en haut de la page publique.</p>
                </label>
                <label className="field">
                  <span className="field-label">Date prévue d&apos;arrivée</span>
                  <input
                    className="input"
                    type="date"
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                  />
                  <p className="field-hint">Optionnelle. Affichée sur la page publique.</p>
                </label>
              </div>

              <label className="field">
                <span className="field-label">Mot d&apos;accueil</span>
                <textarea
                  className="input os-textarea"
                  rows={4}
                  value={welcomeMessage}
                  onChange={(e) => setWelcomeMessage(e.target.value)}
                />
                <p className="field-hint">
                  Quelques phrases que vos invités liront en premier.{' '}
                  <span className="muted">{welcomeMessage.length} / 2000</span>
                </p>
              </label>

              <div className="field">
                <span className="field-label">Image de couverture</span>
                <div className="os-cover-row">
                  <div className="os-cover-thumb" aria-hidden="true">
                    {coverUrl ? (
                      <img src={coverUrl} alt="" />
                    ) : (
                      <div className="os-cover-ph">
                        <span className="os-cover-mono">cover.jpg</span>
                        <span className="os-cover-mono-sub">1600 × 900</span>
                      </div>
                    )}
                  </div>
                  <div className="os-cover-actions">
                    <input
                      ref={coverFileRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/avif"
                      hidden
                      onChange={(e) => {
                        const f = e.target.files?.[0]
                        if (f) void onUploadCover(f)
                        if (coverFileRef.current) coverFileRef.current.value = ''
                      }}
                    />
                    <button
                      type="button"
                      className="btn-secondary btn-sm"
                      onClick={() => coverFileRef.current?.click()}
                    >
                      {coverUrl ? "Changer l'image" : 'Téléverser une image'}
                    </button>
                    <p className="field-hint">
                      JPG, PNG, WebP ou AVIF. Max 8 Mo. Format paysage recommandé.
                    </p>
                  </div>
                </div>
              </div>

              <SaveBar
                state={saveGeneral}
                onSave={() => {
                  const patch: UpdateRegistryRequest = {
                    title: regTitle,
                    dueDate: dueDate.trim() === '' ? null : dueDate,
                    welcomeMessage: normNullable(welcomeMessage),
                  }
                  void savePatch(setSaveGeneral, patch)
                }}
              />
            </Section>

            <Section id="paiement" eyebrow="02" title="Paiement" isMobile={isMobile}>
              <div className="os-callout">
                <span className="os-callout-mark" aria-hidden="true">
                  i
                </span>
                <p>
                  Vos invités paieront <em>directement</em> sur ces comptes — nous ne traitons pas
                  les paiements.
                </p>
              </div>

              <div className="os-paygroup">
                <p className="os-paytitle">Virement (IBAN)</p>
                <div className="os-grid2">
                  <label className="field os-wide">
                    <span className="field-label">IBAN</span>
                    <input
                      className="input os-mono"
                      value={iban}
                      onChange={(e) => setIban(fmtIban(e.target.value))}
                    />
                  </label>
                  <label className="field">
                    <span className="field-label">BIC / SWIFT</span>
                    <input
                      className="input os-mono"
                      value={bic}
                      onChange={(e) => setBic(e.target.value.toUpperCase())}
                    />
                  </label>
                </div>
                <label className="field">
                  <span className="field-label">Titulaire du compte</span>
                  <input
                    className="input"
                    value={ibanHolder}
                    onChange={(e) => setIbanHolder(e.target.value)}
                  />
                </label>
              </div>

              <div className="os-paygroup">
                <p className="os-paytitle">Applis mobiles</p>
                <div className="os-grid2">
                  <label className="field">
                    <span className="field-label">Lydia · numéro</span>
                    <input
                      className="input"
                      type="tel"
                      value={lydiaPhone}
                      onChange={(e) => setLydiaPhone(e.target.value)}
                    />
                    <p className="field-hint">Optionnel.</p>
                  </label>
                  <label className="field">
                    <span className="field-label">Wero · numéro</span>
                    <input
                      className="input"
                      type="tel"
                      value={weroPhone}
                      onChange={(e) => setWeroPhone(e.target.value)}
                    />
                  </label>
                </div>
                <label className="field">
                  <span className="field-label">PayPal · email</span>
                  <input
                    className="input"
                    type="email"
                    value={paypalEmail}
                    onChange={(e) => setPaypalEmail(e.target.value)}
                  />
                  <p className="field-hint">Laissez vide pour ne pas proposer PayPal.</p>
                </label>
              </div>

              <SaveBar
                state={savePayment}
                onSave={() =>
                  void savePatch(setSavePayment, {
                    iban: normNullable(iban),
                    bic: normNullable(bic),
                    ibanHolder: normNullable(ibanHolder),
                    lydiaPhone: normNullable(lydiaPhone),
                    weroPhone: normNullable(weroPhone),
                    paypalEmail: paypalEmail.trim() === '' ? null : paypalEmail.trim(),
                  })
                }
              />
            </Section>

            <Section
              id="equipe"
              eyebrow="03"
              title="Équipe"
              sub="Invitez un co-propriétaire pour gérer la liste à deux."
              isMobile={isMobile}
            >
              <p className="os-sub-label">Membres actuels</p>
              <ul className="os-member-list" role="list">
                {members.map((m) => {
                  const name = m.displayName ?? m.email
                  const isSelf = userId
                    ? m.userId === userId
                    : meEmail
                      ? m.email === meEmail
                      : false
                  return (
                    <li key={m.userId} className="os-member">
                      <span className="os-avatar" aria-hidden="true">
                        {(name || '?').slice(0, 1).toUpperCase()}
                      </span>
                      <div className="os-minfo">
                        <p className="os-mname">
                          {name}
                          {isSelf && <span className="os-selftag">vous</span>}
                        </p>
                        <p className="os-mmail">{m.email}</p>
                      </div>
                      <span className="os-mrole">Propriétaire</span>
                      <div className="os-mactions">
                        <button
                          type="button"
                          className="btn-ghost btn-sm os-dangerlink"
                          onClick={() => void removeMember(m.userId)}
                        >
                          {isSelf ? 'Quitter' : 'Retirer'}
                        </button>
                      </div>
                    </li>
                  )
                })}
              </ul>

              {pendingInvites.length > 0 && (
                <>
                  <p className="os-sub-label os-sub-label-spaced">Invitations en attente</p>
                  <ul className="os-member-list" role="list">
                    {pendingInvites.map((inv) => (
                      <li key={inv.id} className="os-member os-member-pending">
                        <span className="os-avatar os-avatar-pending" aria-hidden="true">
                          ·
                        </span>
                        <div className="os-minfo">
                          <p className="os-mname os-muted-italic">{inv.email}</p>
                          <p className="os-mmail">Invitation envoyée</p>
                        </div>
                        <span className="os-mrole os-role-pending">En attente</span>
                        <div className="os-mactions">
                          <button
                            type="button"
                            className="btn-ghost btn-sm"
                            onClick={() => void cancelInvite(inv.id)}
                          >
                            Annuler
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                </>
              )}

              <p className="os-sub-label os-sub-label-spaced">Inviter un co-propriétaire</p>
              <div className="os-invite-row">
                <input
                  className={`input ${inviteErr ? 'os-input-err' : ''}`}
                  type="email"
                  placeholder="email@exemple.fr"
                  value={inviteEmail}
                  onChange={(e) => {
                    setInviteEmail(e.target.value)
                    setInviteErr(null)
                  }}
                  aria-invalid={inviteErr ? true : undefined}
                />
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  onClick={() => void invite()}
                  disabled={saveTeam === 'saving'}
                >
                  {saveTeam === 'saving' ? 'Envoi…' : "Envoyer l'invitation"}
                </button>
              </div>
              {inviteErr && <p className="os-hint os-hint-err">{inviteErr}</p>}
              {teamActionErr && <p className="os-hint os-hint-err">{teamActionErr}</p>}
              <p className="os-hint">
                Un lien d&apos;invitation sera envoyé. La personne devra créer un compte (ou se
                connecter) pour rejoindre la liste.
              </p>

              <SaveBar state={saveTeam} onSave={() => scheduleSaved(setSaveTeam)} />
            </Section>

            <Section
              id="theme"
              eyebrow="04"
              title="Thème"
              sub="Choisissez l'apparence de votre page publique."
              isMobile={isMobile}
            >
              <div className="os-theme-grid">
                {THEME_OPTIONS.map((th) => {
                  const active = th.id === theme
                  return (
                    <button
                      key={th.id}
                      type="button"
                      className={`os-theme-card ${active ? 'os-theme-card-on' : ''}`}
                      onClick={() => setTheme(th.id)}
                      aria-pressed={active}
                    >
                      <div
                        className="os-theme-prev"
                        style={{ background: th.preview.bg, color: th.preview.ink }}
                      >
                        <span className="os-theme-prev-ey" style={{ color: th.preview.accent }}>
                          aperçu
                        </span>
                        <span
                          className="os-theme-prev-title"
                          style={{ fontFamily: th.preview.fontFamily }}
                        >
                          {regTitle || 'Liste'}
                        </span>
                        <span
                          className="os-theme-prev-line"
                          style={{ background: th.preview.ink, opacity: 0.15 }}
                        />
                        <span
                          className="os-theme-prev-line os-short"
                          style={{ background: th.preview.ink, opacity: 0.15 }}
                        />
                        <span
                          className="os-theme-prev-dot"
                          style={{ background: th.preview.accent }}
                        />
                      </div>
                      <div className="os-theme-meta">
                        <p className="os-theme-name">{th.name}</p>
                        <p className="os-theme-sub">{th.sub}</p>
                      </div>
                      {active && (
                        <span className="os-theme-check" aria-hidden="true">
                          ✓
                        </span>
                      )}
                    </button>
                  )
                })}
              </div>

              <div className="os-accent-block">
                <p className="os-sub-label">
                  Accent — appliqué au thème{' '}
                  <em>{THEME_OPTIONS.find((t) => t.id === theme)?.name}</em>
                </p>
                <div className="os-accent-row" role="radiogroup" aria-label="Couleur d'accent">
                  {ACCENTS.map((a) => {
                    const active = a.value === (accentColor ?? '')
                    return (
                      <button
                        key={a.value}
                        type="button"
                        role="radio"
                        aria-checked={active}
                        className={`os-accent ${active ? 'os-accent-on' : ''}`}
                        onClick={() => setAccentColor(a.value)}
                        title={a.name}
                      >
                        <span className="os-accent-dot" style={{ background: a.value }} />
                        <span className="os-accent-name">{a.name}</span>
                      </button>
                    )
                  })}
                  <button
                    type="button"
                    role="radio"
                    aria-checked={accentColor === null}
                    className={`os-accent ${accentColor === null ? 'os-accent-on' : ''}`}
                    onClick={() => setAccentColor(null)}
                    title="Par défaut"
                  >
                    <span className="os-accent-dot os-accent-dot-empty" aria-hidden="true" />
                    <span className="os-accent-name">Par défaut</span>
                  </button>
                </div>
                <p className="os-hint">Six teintes choisies pour rester lisibles.</p>
              </div>

              <SaveBar
                state={saveTheme}
                onSave={() => void savePatch(setSaveTheme, { theme, accentColor })}
              />
            </Section>

            <Section
              id="notifications"
              eyebrow="05"
              title="Notifications"
              sub="Vous recevez un email à chaque participation. Ajoutez d'autres adresses si vous voulez les notifier aussi."
              isMobile={isMobile}
            >
              <div className="field">
                <span className="field-label">Adresses notifiées en plus de la vôtre</span>
                <div
                  className={`os-chip-input ${extraErr ? 'os-input-err' : ''}`}
                  onClick={() => extraInputRef.current?.focus()}
                >
                  {extraEmails.map((e) => (
                    <span key={e} className="os-chip">
                      {e}
                      <button
                        type="button"
                        className="os-chip-x"
                        aria-label={`Retirer ${e}`}
                        onClick={(ev) => {
                          ev.stopPropagation()
                          setExtraEmails((arr) => arr.filter((x) => x !== e))
                        }}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                  <input
                    ref={extraInputRef}
                    className="os-chip-field"
                    type="email"
                    placeholder={extraEmails.length ? '' : 'ajouter une adresse, puis Entrée'}
                    value={extraDraft}
                    onChange={(e) => {
                      setExtraDraft(e.target.value)
                      setExtraErr(null)
                    }}
                    onBlur={notifCommitDraft}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ',') {
                        e.preventDefault()
                        notifCommitDraft()
                      } else if (
                        e.key === 'Backspace' &&
                        extraDraft === '' &&
                        extraEmails.length > 0
                      ) {
                        setExtraEmails((arr) => arr.slice(0, -1))
                      }
                    }}
                  />
                </div>
                {extraErr && <p className="field-error">{extraErr}</p>}
                <p className="field-hint">Appuyez sur Entrée ou virgule. Maximum 10.</p>
              </div>

              <div className="os-implicit">
                <div className="os-implicit-check" aria-hidden="true">
                  ✓
                </div>
                <div>
                  <p className="os-implicit-title">Tout participant me notifie</p>
                  <p className="os-implicit-sub">
                    Activé par défaut — vous recevrez un email à chaque cadeau offert.
                  </p>
                </div>
              </div>

              <SaveBar
                state={saveNotif}
                onSave={() =>
                  void savePatch(setSaveNotif, { extraNotificationEmails: extraEmails })
                }
              />
            </Section>

            <Section id="danger" eyebrow="06" title="Zone dangereuse" isMobile={isMobile}>
              <div className="os-danger-card">
                <div>
                  <h3 className="os-danger-title">Supprimer cette liste</h3>
                  <p className="os-danger-sub">
                    La liste, les cadeaux et les participations seront définitivement effacés.{' '}
                    <strong>Cette action est irréversible.</strong>
                  </p>
                </div>

                {!deleteOpen ? (
                  <button type="button" className="btn-danger" onClick={() => setDeleteOpen(true)}>
                    Supprimer la liste
                  </button>
                ) : (
                  <div className="os-danger-confirm">
                    <p className="os-danger-prompt">
                      Pour confirmer, tapez l&apos;adresse de la liste : <code>{slug}</code>
                    </p>
                    <input
                      className={`input os-mono ${deleteConfirm && !deleteMatches ? 'os-input-err' : ''}`}
                      value={deleteConfirm}
                      onChange={(e) => setDeleteConfirm(e.target.value)}
                      placeholder={slug}
                      autoFocus
                    />
                    {deleteErr && <p className="field-error">{deleteErr}</p>}
                    <div className="os-danger-actions">
                      <button
                        type="button"
                        className="btn-ghost btn-sm"
                        onClick={() => {
                          setDeleteOpen(false)
                          setDeleteConfirm('')
                          setDeleteErr(null)
                        }}
                      >
                        Annuler
                      </button>
                      <button
                        type="button"
                        className="btn-danger btn-sm"
                        disabled={!deleteMatches}
                        onClick={() => void doDelete()}
                      >
                        Supprimer définitivement
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </Section>

            <p className="os-bottom">Vos modifications sont enregistrées section par section.</p>
          </div>
        </main>
      </div>
    </div>
  )
}
